import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, CheckCircle2, XCircle, Inbox, FileWarning, Link as LinkIcon, Store, Map as MapIcon, Users, ExternalLink, BellRing, Flag, EyeOff } from "lucide-react";
import api from "../lib/api";
import { PageHeader, Spinner, Pill } from "../components/common";
import { toast } from "sonner";

/* Community UGC reports (posts & photos flagged by travelers or the mobile app). */
function UgcReportsQueue() {
  const [items, setItems] = useState(null);
  const load = () => api.get("/community/reports?status=open").then((r) => setItems(r.data.items)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const resolve = async (rid, action) => {
    try {
      await api.post(`/community/reports/${rid}/resolve`, { action });
      toast.success(action === "hide" ? "Content hidden" : "Report dismissed");
      load(); // resolving one report resolves siblings on the same target
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="mb-8" data-testid="ugc-reports-queue">
      <h2 className="font-heading font-900 text-lg mb-3 flex items-center gap-2">
        <Flag className="w-5 h-5 text-[#EA4335]" /> Community reports
        {items && items.length > 0 && (
          <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-800">{items.length} open</span>
        )}
      </h2>
      {!items ? <Spinner /> : items.length === 0 ? (
        <p className="text-sm text-muted-foreground border border-dashed border-zinc-300 p-4">
          No open reports — the community is behaving.
        </p>
      ) : (
        <div className="space-y-3">
          {items.map((r) => {
            const t = r.target || {};
            return (
              <div key={r.id} className="border border-zinc-300 bg-white p-4" data-testid={`ugc-report-${r.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0 flex gap-3">
                    {t.kind === "photo" && (
                      <img src={`${process.env.REACT_APP_BACKEND_URL}/api/community/photos/${t.id}/image`}
                        alt="" className="w-16 h-16 object-cover border border-zinc-200 shrink-0" />
                    )}
                    <div className="min-w-0">
                      <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1">
                        {t.kind === "photo" ? "Photo" : "Post"} by {t.user_name || "Unknown"}
                        {(t.hidden || t.is_deleted) && <span className="ml-2 text-red-600">already hidden</span>}
                      </div>
                      <p className="text-sm text-zinc-800 line-clamp-2">{t.text || t.caption || "(no text)"}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Reason: <b>{r.reason || "not given"}</b> · {new Date(r.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button onClick={() => resolve(r.id, "dismiss")} data-testid={`ugc-dismiss-${r.id}`}
                      className="inline-flex items-center gap-1.5 px-3 py-2 border border-zinc-300 text-sm font-700 hover:bg-zinc-50">
                      <CheckCircle2 className="w-4 h-4 text-[#34A853]" /> Dismiss
                    </button>
                    <button onClick={() => resolve(r.id, "hide")} data-testid={`ugc-hide-${r.id}`}
                      className="inline-flex items-center gap-1.5 px-3 py-2 bg-[#EA4335] text-white text-sm font-700 hover:opacity-90">
                      <EyeOff className="w-4 h-4" /> Hide content
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* Sign-up email notifications — live toggle, no redeploy needed. */function SignupNotifyCard() {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/email/signup-notify").then((r) => setCfg(r.data)).catch(() => {}); }, []);
  if (!cfg) return null;
  const save = async (next) => {
    setBusy(true);
    try {
      const { data } = await api.put("/email/signup-notify", next);
      setCfg(data);
      toast.success(data.enabled ? `Sign-up alerts ON → ${data.email}` : "Sign-up alerts turned off");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    setBusy(false);
  };
  return (
    <div className="border border-zinc-200 bg-white p-4 mb-8" data-testid="signup-notify-card">
      <div className="flex flex-wrap items-center gap-3">
        <BellRing className={`w-5 h-5 ${cfg.enabled ? "text-emerald-600" : "text-zinc-400"}`} />
        <div className="min-w-0">
          <div className="font-heading font-900 text-sm">New sign-up email alerts</div>
          <p className="text-xs text-muted-foreground">Get an email every time someone joins. Switch off any time — takes effect immediately.</p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <input value={cfg.email} onChange={(e) => setCfg((c) => ({ ...c, email: e.target.value }))}
            placeholder="you@example.com" data-testid="signup-notify-email"
            className="border border-zinc-300 px-2.5 py-1.5 text-sm w-56" />
          <button onClick={() => save({ enabled: !cfg.enabled, email: cfg.email })} disabled={busy}
            data-testid="signup-notify-toggle"
            className={`px-4 py-1.5 text-sm font-800 border transition-colors disabled:opacity-50 ${cfg.enabled
              ? "border-emerald-500 bg-emerald-600 text-white hover:bg-emerald-700"
              : "border-zinc-400 text-zinc-600 hover:border-emerald-500 hover:text-emerald-700"}`}>
            {cfg.enabled ? "ON — click to stop" : "OFF — click to start"}
          </button>
          {cfg.enabled && (
            <button onClick={() => save({ enabled: true, email: cfg.email })} disabled={busy}
              data-testid="signup-notify-save" title="Save email address"
              className="px-3 py-1.5 text-sm font-700 border border-zinc-300 hover:border-blue-500">Save</button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Moderation() {
  const [queue, setQueue] = useState(null);
  const [reports, setReports] = useState(null);
  const [linkAlerts, setLinkAlerts] = useState(null);
  const [claims, setClaims] = useState(null);
  const [mapSuggestions, setMapSuggestions] = useState(null);
  const [sharedLinks, setSharedLinks] = useState(null);

  const load = () => api.get("/complaints/moderation/queue").then((r) => setQueue(r.data));
  const loadReports = () => api.get("/transport/reports").then((r) => setReports(r.data.items)).catch(() => setReports([]));
  const loadLinkAlerts = () => api.get("/visas/link-alerts").then((r) => setLinkAlerts(r.data.items)).catch(() => setLinkAlerts([]));
  const loadClaims = () => api.get("/marketplace/claims").then((r) => setClaims(r.data.items)).catch(() => setClaims([]));
  const loadMapSuggestions = () => api.get("/airport-maps/suggestions").then((r) => setMapSuggestions(r.data.items)).catch(() => setMapSuggestions([]));
  const loadSharedLinks = () => api.get("/trip-visas/links/review").then((r) => setSharedLinks(r.data.items)).catch(() => setSharedLinks([]));
  useEffect(() => { load(); loadReports(); loadLinkAlerts(); loadClaims(); loadMapSuggestions(); loadSharedLinks(); }, []);

  const actSharedLink = async (lid, action) => {
    try {
      await api.post(`/trip-visas/links/${lid}/moderate`, { action });
      toast.success(action === "approve" ? "Link approved — now visible to all travellers" : "Link rejected");
      setSharedLinks((l) => l.filter((x) => x.id !== lid));
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const actMapSuggestion = async (sid, approve) => {
    try {
      await api.post(`/airport-maps/suggestions/${sid}/decide`, { approve });
      toast.success(`Map suggestion ${approve ? "approved — now live on the airport page" : "rejected"}`);
      setMapSuggestions((l) => l.filter((x) => x.id !== sid));
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const actClaim = async (cid, action) => {
    try {
      await api.post(`/marketplace/claims/${cid}/decide`, { action });
      toast.success(`Claim ${action}d`);
      setClaims((l) => l.filter((x) => x.id !== cid));
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const act = async (cid, action) => {
    try {
      await api.post(`/complaints/${cid}/moderate`, { action });
      toast.success(`Complaint ${action}ed`);
      setQueue((q) => q.filter((c) => c.complaint_id !== cid));
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const actReport = async (rid, status) => {
    try {
      await api.patch(`/transport/reports/${rid}`, { status });
      toast.success(`Correction ${status}`);
      setReports((r) => r.filter((x) => x.id !== rid));
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const actLinkAlert = async (aid, status) => {
    try {
      await api.patch(`/visas/link-alerts/${aid}`, { status });
      toast.success(`Link alert ${status}`);
      setLinkAlerts((l) => l.filter((x) => x.id !== aid));
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="moderation-page">
      <PageHeader plain eyebrow="Restricted" title="Moderation Queue"
        subtitle="Review pending complaints. Publish accurate submissions, reject abusive or defamatory ones." />
      <div className="max-w-4xl mx-auto px-5 lg:px-8 pb-16">
        <SignupNotifyCard />
        <UgcReportsQueue />
        {!queue ? <Spinner /> : queue.length === 0 ? (
          <div className="border border-dashed border-zinc-300 py-20 text-center">
            <Inbox className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">Queue is empty. Nothing pending review.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {queue.map((c) => (
              <div key={c.complaint_id} className="border border-zinc-300 bg-white p-5" data-testid={`queue-item-${c.complaint_id}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Pill tone="amber">Pending</Pill><Pill tone="red">{c.category}</Pill><Pill>{c.entity_name}</Pill>
                </div>
                <Link to={`/complaints/${c.complaint_id}`} className="font-heading font-700 text-lg hover:text-primary transition-colors">{c.title}</Link>
                <p className="text-sm text-muted-foreground mt-1 line-clamp-3">{c.body}</p>
                <div className="text-xs font-mono text-muted-foreground mt-2">by {c.author_name} · {new Date(c.created_at).toLocaleString()}</div>
                <div className="flex gap-2 mt-4">
                  <button onClick={() => act(c.complaint_id, "publish")} data-testid={`publish-${c.complaint_id}`}
                    className="inline-flex items-center gap-1 px-4 py-2 text-sm bg-primary text-white hover:bg-blue-700 transition-colors"><CheckCircle2 className="w-4 h-4" /> Publish</button>
                  <button onClick={() => act(c.complaint_id, "reject")} data-testid={`reject-${c.complaint_id}`}
                    className="inline-flex items-center gap-1 px-4 py-2 text-sm border border-destructive text-destructive hover:bg-red-50 transition-colors"><XCircle className="w-4 h-4" /> Reject</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Business claims queue — Airport Marketplace */}
        <div className="mt-12" data-testid="business-claims-queue">
          <h2 className="font-heading font-900 text-2xl tracking-tight mb-1 flex items-center gap-2">
            <Store className="w-6 h-6 text-emerald-700" /> Business claims
          </h2>
          <p className="text-sm text-muted-foreground mb-5">Airport marketplace ownership claims. Verify the business email against the brand's official domain before approving — approval hands the profile to the claimant.</p>
          {!claims ? <Spinner /> : claims.length === 0 ? (
            <div className="border border-dashed border-zinc-300 py-10 text-center text-muted-foreground text-sm">No pending business claims.</div>
          ) : (
            <div className="space-y-3">
              {claims.map((c) => (
                <div key={c.id} className="border border-zinc-300 bg-white p-4" data-testid={`claim-item-${c.id}`}>
                  <div className="flex items-center gap-2 flex-wrap mb-1.5">
                    <Pill tone="amber">Pending</Pill>
                    <Pill tone="blue">{c.airport_iata}</Pill>
                    <Link to={`/business/${c.business_id}`} className="font-heading font-700 hover:text-primary transition-colors">{c.business_name}</Link>
                  </div>
                  <p className="text-sm text-zinc-800">{c.contact_name} ({c.role}) — <span className="font-mono">{c.business_email}</span></p>
                  {c.message && <p className="text-sm text-muted-foreground mt-1">{c.message}</p>}
                  <div className="text-xs font-mono text-muted-foreground mt-1.5">account: {c.user_email} · {new Date(c.created_at).toLocaleString()}</div>
                  <div className="flex gap-2 mt-3">
                    <button onClick={() => actClaim(c.id, "approve")} data-testid={`claim-approve-${c.id}`}
                      className="inline-flex items-center gap-1 px-3.5 py-1.5 text-sm bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"><CheckCircle2 className="w-4 h-4" /> Approve & verify</button>
                    <button onClick={() => actClaim(c.id, "reject")} data-testid={`claim-reject-${c.id}`}
                      className="inline-flex items-center gap-1 px-3.5 py-1.5 text-sm border border-destructive text-destructive hover:bg-red-50 transition-colors"><XCircle className="w-4 h-4" /> Reject</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Airport map suggestions — traveler-submitted map links */}
        <div className="mt-12" data-testid="map-suggestions-queue">
          <h2 className="font-heading font-900 text-2xl tracking-tight mb-1 flex items-center gap-2">
            <MapIcon className="w-6 h-6 text-blue-700" /> Airport map suggestions
          </h2>
          <p className="text-sm text-muted-foreground mb-5">Traveler-submitted airport map links. Open each link and confirm it is a real, current map for the right airport before approving — approval publishes it on the airport page as a third-party map.</p>
          {!mapSuggestions ? <Spinner /> : mapSuggestions.length === 0 ? (
            <div className="border border-dashed border-zinc-300 py-10 text-center text-muted-foreground text-sm">No pending map suggestions.</div>
          ) : (
            <div className="space-y-3">
              {mapSuggestions.map((s) => (
                <div key={s.id} className="border border-zinc-300 bg-white p-4" data-testid={`map-suggestion-${s.id}`}>
                  <div className="flex items-center gap-2 flex-wrap mb-1.5">
                    <Pill tone="amber">Pending</Pill>
                    <Pill tone="blue">{s.iata}</Pill>
                    <span className="font-heading font-700">{s.map_name}</span>
                  </div>
                  <a href={s.map_url} target="_blank" rel="noreferrer" className="text-sm text-primary font-mono break-all hover:underline">{s.map_url}</a>
                  {s.notes && <p className="text-sm text-muted-foreground mt-1">{s.notes}</p>}
                  <div className="text-xs font-mono text-muted-foreground mt-1.5">by {s.user_name || s.user_id} · {s.created_at && new Date(s.created_at).toLocaleString()}</div>
                  <div className="flex gap-2 mt-3">
                    <button onClick={() => actMapSuggestion(s.id, true)} data-testid={`map-suggestion-approve-${s.id}`}
                      className="inline-flex items-center gap-1 px-3.5 py-1.5 text-sm bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"><CheckCircle2 className="w-4 h-4" /> Approve & publish</button>
                    <button onClick={() => actMapSuggestion(s.id, false)} data-testid={`map-suggestion-reject-${s.id}`}
                      className="inline-flex items-center gap-1 px-3.5 py-1.5 text-sm border border-destructive text-destructive hover:bg-red-50 transition-colors"><XCircle className="w-4 h-4" /> Reject</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Data corrections queue (§34) — visas + transport directory reports */}
        <div className="mt-12" data-testid="corrections-queue">
          <h2 className="font-heading font-900 text-2xl tracking-tight mb-1 flex items-center gap-2">
            <FileWarning className="w-6 h-6 text-[#0e7490]" /> Data corrections queue
          </h2>
          <p className="text-sm text-muted-foreground mb-5">User-submitted corrections for visas, embassies and transport directory records. Verify against official sources before resolving.</p>
          {!reports ? <Spinner /> : reports.length === 0 ? (
            <div className="border border-dashed border-zinc-300 py-10 text-center text-muted-foreground text-sm">No pending corrections.</div>
          ) : (
            <div className="space-y-3">
              {reports.map((r) => (
                <div key={r.id} className="border border-zinc-300 bg-white p-4" data-testid={`report-item-${r.id}`}>
                  <div className="flex items-center gap-2 flex-wrap mb-1.5">
                    <Pill tone="amber">Pending</Pill>
                    <Pill tone="blue">{r.mode}</Pill>
                    <Pill>{r.entity_type}: {r.entity_id}</Pill>
                    <Pill tone="red">{(r.issue_type || "").replaceAll("_", " ")}</Pill>
                  </div>
                  <p className="text-sm text-zinc-800">{r.detail}</p>
                  <div className="text-xs font-mono text-muted-foreground mt-1.5">{r.created_at && new Date(r.created_at).toLocaleString()}</div>
                  <div className="flex gap-2 mt-3">
                    <button onClick={() => actReport(r.id, "resolved")} data-testid={`report-resolve-${r.id}`}
                      className="inline-flex items-center gap-1 px-3.5 py-1.5 text-sm bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"><CheckCircle2 className="w-4 h-4" /> Resolved</button>
                    <button onClick={() => actReport(r.id, "rejected")} data-testid={`report-reject-${r.id}`}
                      className="inline-flex items-center gap-1 px-3.5 py-1.5 text-sm border border-destructive text-destructive hover:bg-red-50 transition-colors"><XCircle className="w-4 h-4" /> Reject</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Broken official links (§32) — flagged by the daily link monitor cron */}
        <div className="mt-12" data-testid="link-alerts-queue">
          <h2 className="font-heading font-900 text-2xl tracking-tight mb-1 flex items-center gap-2">
            <LinkIcon className="w-6 h-6 text-rose-600" /> Broken official links
          </h2>
          <p className="text-sm text-muted-foreground mb-5">The daily monitor checks official government and mission links in rotating batches. Verify each on the responsible official domain — never substitute a commercial site.</p>
          {!linkAlerts ? <Spinner /> : linkAlerts.length === 0 ? (
            <div className="border border-dashed border-zinc-300 py-10 text-center text-muted-foreground text-sm">No broken links detected.</div>
          ) : (
            <div className="space-y-3">
              {linkAlerts.map((a) => (
                <div key={a.id} className="border border-zinc-300 bg-white p-4" data-testid={`link-alert-${a.id}`}>
                  <div className="flex items-center gap-2 flex-wrap mb-1.5">
                    {a.alert_kind === "content_changed"
                      ? <Pill tone="amber">Content changed</Pill>
                      : <Pill tone="red">{a.status_code ? `HTTP ${a.status_code}` : "Unreachable"}</Pill>}
                    <Pill tone="blue">{a.entity_type}</Pill>
                    <Pill>{a.entity_name}</Pill>
                    <Pill tone="muted">{a.link_key}</Pill>
                  </div>
                  <a href={a.url} target="_blank" rel="noreferrer" className="text-sm text-primary font-mono break-all hover:underline">{a.url}</a>
                  {a.error && <p className="text-xs text-muted-foreground mt-1">{a.error}</p>}
                  <div className="text-xs font-mono text-muted-foreground mt-1.5">First detected {a.first_detected && new Date(a.first_detected).toLocaleString()}</div>
                  <div className="flex gap-2 mt-3">
                    <button onClick={() => actLinkAlert(a.id, "resolved")} data-testid={`link-alert-resolve-${a.id}`}
                      className="inline-flex items-center gap-1 px-3.5 py-1.5 text-sm bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"><CheckCircle2 className="w-4 h-4" /> Fixed</button>
                    <button onClick={() => actLinkAlert(a.id, "dismissed")} data-testid={`link-alert-dismiss-${a.id}`}
                      className="inline-flex items-center gap-1 px-3.5 py-1.5 text-sm border border-zinc-400 text-zinc-600 hover:bg-zinc-50 transition-colors"><XCircle className="w-4 h-4" /> Dismiss</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Community-shared visa & entry links awaiting review */}
        <div className="mt-12" data-testid="shared-links-queue">
          <h2 className="font-heading font-900 text-2xl tracking-tight mb-1 flex items-center gap-2">
            <Users className="w-6 h-6 text-sky-600" /> Community visa links
          </h2>
          <p className="text-sm text-muted-foreground mb-5">Travellers offered to share these visa &amp; entry links with the whole community. Open each link, confirm it is a legitimate official or genuinely useful resource, then approve or reject.</p>
          {!sharedLinks ? <Spinner /> : sharedLinks.length === 0 ? (
            <div className="border border-dashed border-zinc-300 py-10 text-center text-muted-foreground text-sm">No shared links awaiting review.</div>
          ) : (
            <div className="space-y-3">
              {sharedLinks.map((l) => (
                <div key={l.id} className="border border-zinc-300 bg-white p-4" data-testid={`shared-link-${l.id}`}>
                  <div className="flex items-center gap-2 flex-wrap mb-1.5">
                    <Pill tone="blue">{l.country}</Pill>
                    <span className="font-heading font-700">{l.title}</span>
                    {l.date_posted && <span className="text-xs text-muted-foreground">posted {l.date_posted}</span>}
                  </div>
                  <a href={l.url} target="_blank" rel="noreferrer" className="text-sm text-primary font-mono break-all hover:underline inline-flex items-center gap-1">{l.url} <ExternalLink className="w-3.5 h-3.5 shrink-0" /></a>
                  {l.notes && <p className="text-xs text-muted-foreground mt-1">{l.notes}</p>}
                  <div className="flex gap-2 mt-3">
                    <button onClick={() => actSharedLink(l.id, "approve")} data-testid={`shared-link-approve-${l.id}`}
                      className="inline-flex items-center gap-1 px-3.5 py-1.5 text-sm bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"><CheckCircle2 className="w-4 h-4" /> Approve</button>
                    <button onClick={() => actSharedLink(l.id, "reject")} data-testid={`shared-link-reject-${l.id}`}
                      className="inline-flex items-center gap-1 px-3.5 py-1.5 text-sm border border-destructive text-destructive hover:bg-red-50 transition-colors"><XCircle className="w-4 h-4" /> Reject</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
